"use client";
import axios from "axios";

import React, { FC, PropsWithChildren, useRef, useEffect } from "react";

import {
  DisplayItem,
  SortedItemGetter,
  MarketInsightsSortType,
  InstrumentSearch,
  InstrumentSearchResult,
  SortCategoryId,
  SortCategoryItem,
} from "../types";
import { useToken } from "./AuthContext";
import { useAppLoadingContext } from "./AppLoadingContext";
import { useSortContext } from "./SortContext";
import { getApiBase } from "@iliotech/watchlist-api/src/utils/watchlist-api";
import { useSortCategory } from "../hooks/useSortCategory";
import { fetchSortedItems } from "../hooks/useSortedItems";
import { useMemoryCache } from "../hooks/useMemoryCache";
import { CATEGORY_ITEMS_ORDER } from "./CategoryItemsOrder";
import { useMarketCategoryRankingsForOptions } from "../hooks/useMarketCategoryRankingsForOptions";
import { NumericMarketData } from "../types";
import { getNumberFromString } from "../utils/getNumberFromString";
import { MarketData } from "../types/MarketData";
import { useStockTriggers } from "../hooks/useStockTriggers";
import { MarketDataProvider, useMarketDataContext } from "./MarketDataContext";
const getNumericMarketData = (stock: MarketData): NumericMarketData => {
  const transformed: any = {};

  // Fields to preserve as strings
  const preservedFields = [
    "icon",
    "name",
    "label",
    "Sector",
    "instrumentId",
    "Earning Days",
  ];

  // Transform all fields in the MarketData object
  Object.keys(stock).forEach((key) => {
    const value = stock[key as keyof MarketData];

    if (preservedFields.includes(key)) {
      // Keep these fields as they are (strings or other types)
      transformed[key] = value;
    } else if (typeof value === "string") {
      // Transform string values to numbers using getNumberFromString
      transformed[key] = getNumberFromString(value);
    } else if (value === null) {
      // Handle null values by converting to 0
      transformed[key] = null;
    } else {
      // Keep other non-string values as they are
      transformed[key] = value;
    }
  });

  return transformed as NumericMarketData;
};
export interface IBHorizontalMenuItem {
  title: string;
  subItems?: IBHorizontalMenuItem[];
  onClick?: () => void;
}

interface IOwnProps {
  getSortedItems?: SortedItemGetter;
  searchInstruments?: InstrumentSearch;
  onInsightsButtonClick?: () => void;
  onHomeClick?: () => void;
  onInstrumentClick(displayItem: DisplayItem, useWatchlist?: boolean): void;
  onTableButtonClick?: () => void;
}

const MarketContext =
  React.createContext<
    | (ReturnType<typeof useMarketContextValue> &
        Pick<
          IOwnProps,
          | "onInsightsButtonClick"
          | "onInstrumentClick"
          | "onHomeClick"
          | "onTableButtonClick"
        >)
    | null
  >(null);

export const DEFAULT_SEARCH_INSTRUMENTS: InstrumentSearch = async (
  search: string,
  environment: string = "dev",
  token: string,
): Promise<InstrumentSearchResult[]> => {
  const INSIGHTS_API_BASE = getApiBase(environment);

  const result = await axios.get<InstrumentSearchResult[]>(
    `${INSIGHTS_API_BASE}/instruments?text=${search}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return result?.data;
};

export const MARKET_INITIAL_SORT = {
  categoryId: "RISK",
  subCategoryId: "UPSIDE_IMPACT",
} as const;

export const MarketContextProvider: FC<PropsWithChildren<IOwnProps>> = ({
  children,
  onInsightsButtonClick,
  onInstrumentClick,
  onHomeClick,
  onTableButtonClick,
}) => {
  const { environment } = useToken();
  const { watchlistId } = useSortContext();
  const { data: marketData, isLoading: marketLoading } =
    useMarketCategoryRankingsForOptions({
      watchlistId,
      environment: environment!,
      enabledCategories: [
        SortCategoryId.TECHNICALS,
        SortCategoryId.TRADE_IDEAS,
        SortCategoryId.PERFORMANCE,
        SortCategoryId.FUNDAMENTALS,
        SortCategoryId.OPTIONS,
        SortCategoryId.RISK,
        SortCategoryId.EARNINGS,
      ],
      overrideAllSort: true,
    });

  const numericMarketData = React.useMemo(() => {
    if (!marketData || marketLoading) return [];
    return marketData.map((stock) => getNumericMarketData(stock));
  }, [marketData, marketLoading]);

  console.log({ marketData, numericMarketData, marketLoading });
  return (
    <MarketDataProvider
      numericMarketData={numericMarketData}
      watchlistId={watchlistId || ""}
    >
      <MarketContextProviderInner
        onInsightsButtonClick={onInsightsButtonClick}
        onInstrumentClick={onInstrumentClick}
        onHomeClick={onHomeClick}
        onTableButtonClick={onTableButtonClick}
      >
        {children}
      </MarketContextProviderInner>
    </MarketDataProvider>
  );
};

const MarketContextProviderInner: FC<PropsWithChildren<IOwnProps>> = ({
  children,
  onInsightsButtonClick,
  onInstrumentClick,
  onHomeClick,
  onTableButtonClick,
}) => {
  const value = useMarketContextValue();
  return (
    <MarketContext.Provider
      value={{
        ...value,
        onInsightsButtonClick,
        onInstrumentClick,
        onHomeClick,
        onTableButtonClick,
      }}
    >
      {children}
    </MarketContext.Provider>
  );
};

const useMarketContextValue = () => {
  const { environment } = useToken();
  const { optimisticSort, setSort, sort, watchlistId } = useSortContext();
  const { data: sortCategories } = useSortCategory(watchlistId, environment);
  const { setLoading, loading } = useAppLoadingContext();
  const [sortTitle, setSortTitle] = React.useState("");
  const watchlistIdRef = useRef(watchlistId);
  const { numericMarketData } = useMarketDataContext();
  const triggersData = useStockTriggers();

  const getPriceFromTicker = (ticker: string) => {
    if (!numericMarketData) return 0;
    const price = numericMarketData.find(
      (stock) => stock.name === ticker,
    )?.price;
    return price || 0;
  };

  const getMetadataFromTicker = (ticker: string) => {
    if (!numericMarketData) return null;
    const metadata = numericMarketData.find((stock) => stock.name === ticker);
    return metadata || null;
  };

  // Setup cached fetch function
  const { cachedFn: fetchData, clearCache: clearFetchCache } = useMemoryCache(
    (categoryId: SortCategoryId) =>
      fetchSortedItems(environment, categoryId, watchlistIdRef.current),
    {
      getCacheKey: (categoryId) => `${categoryId}-${watchlistIdRef.current}`,
    },
  );

  useEffect(() => {
    watchlistIdRef.current = watchlistId;
    // Clear cache when watchlist changes
    clearFetchCache();
    sortInstruments(sort);
  }, [watchlistId, clearFetchCache]);

  const sortCategoryItems = (
    categoryId: SortCategoryId,
    categoryItems: SortCategoryItem[],
  ) => {
    const order = CATEGORY_ITEMS_ORDER[categoryId as SortCategoryId];
    return categoryItems.sort((a, b) => {
      const aIndex = order.indexOf(a.itemId);
      const bIndex = order.indexOf(b.itemId);
      return aIndex - bIndex;
    });
  };

  const sortTriggers = React.useCallback(
    (nextSort: MarketInsightsSortType) => {
      if (!triggersData || triggersData.length === 0) {
        return;
      }

      optimisticSort.setOptimisticSort(nextSort);
      setLoading(true);

      let filteredData: any[] = [];
      let title = nextSort.subCategoryLabel || "";

      if (nextSort.subCategoryId === "GENERAL_TRIGGERS") {
        title = "General Triggers";
        setSortDescription(
          "We analyse a mix of technical triggers and highlight names where one or more triggers are activated.",
        );
        // Filter instruments that have generalStrategies with valid values
        filteredData = triggersData.map((item) => {
          const validGeneralStrategies =
            item.generalStrategies?.filter((s: any) => s.value) || [];
          const validTriggerStrategies =
            item.triggerStrategies?.filter((s: any) => s.value) || [];
          const totalStrategiesCount =
            validGeneralStrategies.length + validTriggerStrategies.length;
          // Show all triggers for this instrument
          const triggers = item.triggers || [];
          return {
            name: item.name,
            icon: item.icon,
            instrumentId: item.metadata?.instrumentId?.toString() || "",
            value: totalStrategiesCount,
            label: item.metadata?.label || item.name,
            price: item.price || 0,
            strategies: validGeneralStrategies, // Keep strategies for later use
            triggerStrategies: item.triggerStrategies || [],
            generalStrategies: item.generalStrategies || [],
            metadata: item.metadata,
            strategyLabels: triggers
              .map((t: any) => t.label || "")
              .filter(Boolean),
            triggers: triggers.map((t: any) => ({
              label: t.label || "",
              dataLessLabel: t.dataLessLabel,
              info: t.info,
            })),
          };
        });
      } else if (nextSort.subCategoryId === "EARNINGS_TRIGGERS") {
        title = "Earnings Triggers";
        setSortDescription(
          "We analyse names that have upcoming Earnings over the next two days. Using in-house quantitative models, we apply probability models to try and evaluate likely price moves both going into Earnings and immediately after. We then also look at available options to provide a one-stop solution to playing the Earnings triggers.",
        );
        // Filter instruments with upcoming earnings (0-5 days) and valid triggerStrategies
        console.log({ triggersData });
        const earningsFiltered = triggersData.filter((item) => {
          const earningDays = item.metadata?.["Earning Days"];
          return (
            earningDays === "0 days" ||
            earningDays === "1 days" ||
            earningDays === "2 days" ||
            earningDays === "3 days" ||
            earningDays === "4 days" ||
            earningDays === "5 days"
          );
        });

        console.log({ earningsFiltered });
        // Sort: Earning Days ascending, then After Results Win Price Change - 1 month descending
        const sortedEarnings = [...earningsFiltered].sort((a, b) => {
          const parseDays = (val: string) => {
            const n = parseFloat((val || "").toString());
            return isNaN(n) ? Number.POSITIVE_INFINITY : n;
          };
          const aDays = parseDays(a.metadata?.["Earning Days"]);
          const bDays = parseDays(b.metadata?.["Earning Days"]);
          if (aDays !== bDays) return aDays - bDays;

          const parseChange = (v: string) => {
            const s = (v || "").toString().replace("%", "");
            const n = parseFloat(s);
            return isNaN(n) ? -Infinity : n;
          };
          const aChange = parseChange(
            a.metadata?.["After Results Win Price Change - 1 month"],
          );
          const bChange = parseChange(
            b.metadata?.["After Results Win Price Change - 1 month"],
          );
          return bChange - aChange;
        });

        console.log({ sortedEarnings });
        filteredData = sortedEarnings.map((item) => {
          const validTriggerStrategies =
            item.triggerStrategies?.filter((s: any) => s.value) || [];
          const validGeneralStrategies =
            item.generalStrategies?.filter((s: any) => s.value) || [];
          const totalStrategiesCount =
            validTriggerStrategies.length + validGeneralStrategies.length;
          // Show all triggers for this instrument
          const triggers = item.triggers || [];
          return {
            name: item.name,
            icon: item.icon,
            instrumentId: item.metadata?.instrumentId?.toString() || "",
            value: totalStrategiesCount,
            label: item.metadata?.label || item.name,
            price: item.price || 0,
            strategies: validTriggerStrategies, // Keep strategies for later use
            triggerStrategies: item.triggerStrategies || [],
            generalStrategies: item.generalStrategies || [],
            metadata: item.metadata,
            strategyLabels: triggers
              .map((t: any) => t.label || "")
              .filter(Boolean),
            triggers: triggers.map((t: any) => ({
              label: t.label || "",
              dataLessLabel: t.dataLessLabel,
              info: t.info,
            })),
          };
        });
      } else if (nextSort.subCategoryId === "INCOME_TRIGGERS") {
        title = "Income Triggers";
        setSortDescription(
          'We analyse 24 possible options trades in order to generate a synthetic income on existing equity holdings. We evaluate and then present names where the maximum number of option trades are viable (viability is based on our internal process), and which are potentially the "best" (the term "best" is subjective and based on our own internal process).',
        );
        // Filter instruments that have incomeGeneration data
        const incomeFiltered = triggersData
          .filter((item) => {
            return (
              item.incomeGeneration &&
              ((item.incomeGeneration.topPuts &&
                item.incomeGeneration.topPuts.length > 0) ||
                (item.incomeGeneration.topCalls &&
                  item.incomeGeneration.topCalls.length > 0))
            );
          })
          .map((item) => {
            const allPuts =
              item.incomeGeneration?.allPuts ||
              item.incomeGeneration?.topPuts ||
              [];
            const allCalls =
              item.incomeGeneration?.allCalls ||
              item.incomeGeneration?.topCalls ||
              [];
            const validPuts = allPuts.filter((s: any) => s.value);
            const validCalls = allCalls.filter((s: any) => s.value);
            const incomeOpportunities = validPuts.length + validCalls.length;

            // For income triggers, show all triggers
            const incomeTriggers = item.triggers || [];
            return {
              name: item.name,
              icon: item.icon,
              instrumentId: item.metadata?.instrumentId?.toString() || "",
              strategies: item.incomeGeneration, // Keep strategies for later use
              value: incomeOpportunities,
              label: item.metadata?.label || item.name,
              price: item.price || 0,
              triggerStrategies: item.triggerStrategies || [],
              generalStrategies: item.generalStrategies || [],
              metadata: item.metadata,
              strategyLabels: incomeTriggers
                .map((t: any) => t.label || "")
                .filter(Boolean),
              triggers: incomeTriggers.map((t: any) => ({
                label: t.label || "",
                dataLessLabel: t.dataLessLabel,
                info: t.info,
              })),
            };
          });

        // Sort by number of income opportunities (descending)
        filteredData = incomeFiltered.sort((a, b) => b.value - a.value);
      }

      setSortTitle(title);
      const parsedRows: {
        instruments: DisplayItem[];
        label: string;
      }[] = [
        {
          label: title,
          instruments: filteredData.map((instrument: any) => ({
            id: instrument.instrumentId,
            instrumentId: instrument.instrumentId,
            icon: instrument.icon || "",
            value: instrument.value,
            name: instrument.name,
            label: instrument.label || instrument.name,
            price: instrument.price,
            strategyLabels: instrument.strategyLabels || [],
            triggers: instrument.triggers || [],
            strategiesCount: instrument.value || 0,
            strategies: instrument.strategies, // For income generation data
            triggerStrategies: instrument.triggerStrategies || [],
            generalStrategies: instrument.generalStrategies || [],
            metadata: instrument.metadata,
          })),
        },
      ];

      setRows(parsedRows);
      setLoading(false);
      setSort(nextSort);
    },
    [triggersData, optimisticSort, setLoading, setSort],
  );

  //@ts-ignore
  const horizontalMenuItems: IBHorizontalMenuItem[] = React.useMemo(() => {
    const categoriesItems =
      sortCategories?.map((category) => ({
        title: category.title,
        selected: sort.categoryId === category.categoryId,
        subItems: sortCategoryItems(
          category.categoryId as SortCategoryId,
          category.categoryItems,
        ).map((item) => {
          const hasChildren = item.children?.length > 0;
          if (hasChildren) {
            return {
              title: item.title,
              subItems: item.children.map((child) => ({
                title: child.title,
                subItems: child.subTitles?.map((subTitle) => ({
                  title: subTitle,
                  onClick: () =>
                    sortInstruments({
                      categoryId: category.categoryId,
                      subCategoryId: item.title,
                      subCategoryLabel: item.title,
                      subSubCategoryLabel: child.title,
                      period: subTitle,
                    }),
                })),
              })),
            };
          }
          return {
            title: item.title,
            onClick: () =>
              item.subTitles?.length
                ? null
                : sortInstruments({
                    categoryId: category.categoryId,
                    subCategoryId: item.title,
                    subCategoryLabel: item.title,
                  }),
            subItems: item.subTitles?.map((subTitle) => ({
              title: subTitle,
              onClick: () =>
                sortInstruments({
                  categoryId: category.categoryId,
                  subCategoryId: item.title,
                  subCategoryLabel: item.title,
                  period: subTitle,
                }),
            })),
          };
        }),
      })) || [];

    // Create fixed triggers menu item
    const triggersMenuItem: IBHorizontalMenuItem = {
      title: "Triggers",
      subItems: [
        {
          title: "General Triggers",
          onClick: () =>
            sortTriggers({
              categoryId: "TRIGGERS",
              subCategoryId: "GENERAL_TRIGGERS",
              subCategoryLabel: "General Triggers",
            }),
        },
        {
          title: "Earnings Triggers",
          onClick: () =>
            sortTriggers({
              categoryId: "TRIGGERS",
              subCategoryId: "EARNINGS_TRIGGERS",
              subCategoryLabel: "Earnings Triggers",
            }),
        },
        {
          title: "Income Triggers",
          onClick: () =>
            sortTriggers({
              categoryId: "TRIGGERS",
              subCategoryId: "INCOME_TRIGGERS",
              subCategoryLabel: "Income Triggers",
            }),
        },
      ],
    };

    return [...categoriesItems, triggersMenuItem];
  }, [sortCategories, sort, sortTriggers]);

  const [sortDescription, setSortDescription] = React.useState<string>();

  const [rows, setRows] = React.useState<
    {
      instruments: DisplayItem[];
      label: string;
    }[]
  >([]);

  React.useEffect(() => {
    if (!!sortTitle) {
      return;
    }
    if (sortCategories?.length === 0 || !sortCategories) {
      return;
    }
    if (!!sort.categoryId) {
      sortInstruments({
        categoryId: sort.categoryId,
        subCategoryId: sort.subCategoryId,
        subCategoryLabel: sort.subCategoryLabel,
        period: sort.period,
      });
      return;
    }
  }, [sortCategories, sort]);

  const sortInstruments = React.useCallback(
    async (nextSort: MarketInsightsSortType) => {
      if (!watchlistIdRef.current) return;

      optimisticSort.setOptimisticSort(nextSort);
      setLoading(true);
      const data = await fetchData(nextSort.categoryId as SortCategoryId);

      if (!data) {
        setLoading(false);
        return;
      }
      const hasSubSubCategory = !!nextSort.subSubCategoryLabel;
      let title = "";
      let description = "";
      let rows = [];

      if (hasSubSubCategory) {
        const foundCategory = data?.find(
          (category) => category.title === nextSort.subCategoryLabel,
        );
        const foundParentChart = foundCategory?.chart.find(
          (chart: any) => chart.subTitle === nextSort.subSubCategoryLabel,
        );
        const foundChart = foundParentChart?.children.find(
          (child: any) => child.title === nextSort.period,
        );

        title = `${nextSort.subCategoryLabel} - ${nextSort.subSubCategoryLabel} ${nextSort.period}`;
        description = foundChart?.whyImportant || "";
        rows = foundChart?.rows || [];
      } else {
        const foundCategory = data?.find(
          (category) => category.title === nextSort.subCategoryLabel,
        );
        const foundChart =
          foundCategory?.chart.find(
            (chart: any) => chart.subTitle === nextSort.period,
          ) || foundCategory?.chart[0];

        rows = foundChart?.rows || [];
        description = foundChart?.whyImportant || "";
        // @ts-ignore
        title = nextSort?.period
          ? `${nextSort.subCategoryLabel} - ${nextSort.period}`
          : nextSort.subCategoryLabel;
      }

      setSortTitle(title);
      setSortDescription(description);
      const parsedRows: {
        instruments: DisplayItem[];
        label: string;
      }[] = rows.map((row: any) => ({
        label: row.label,
        instruments: row.instruments.map((instrument: any) => ({
          ...instrument,
          id: instrument.instrumentId!.toString(),
          instrumentId: instrument.instrumentId!.toString(),
          icon: instrument.icon!,
          value: instrument.value,
          name: instrument.name!,
        })),
      }));

      setRows(parsedRows);
      setLoading(false);
      setSort(nextSort);
    },
    [watchlistIdRef],
  );

  const toggleSort = () => {
    const nextSort = {
      ...sort,
    } as const;
    return sortInstruments(nextSort);
  };

  return {
    sort,
    loading: optimisticSort?.isLoading || loading,
    setLoading,
    toggleSort,
    sortInstruments,
    sortTitle,
    sortDescription,
    sortCategories,
    rows,
    horizontalMenuItems,
    ...optimisticSort,
  };
};

export const useMarketContext = () => {
  const value = React.useContext(MarketContext);
  if (!value) {
    throw new Error("MarketContext is not initialized");
  }
  return value;
};
